import { FaTimes, FaDownload } from "react-icons/fa";
import Pagination from "Components/Pagination";
import { designTokens } from "utils/designSystem";

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "24px",
  },
  container: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: designTokens.shadows.lg,
    width: "100%",
    maxWidth: "960px",
    height: "720px",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px",
    borderBottom: "1px solid #e5e7eb",
  },
  titleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  swatch: {
    width: "14px",
    height: "14px",
    borderRadius: "4px",
  },
  title: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1f2937",
    margin: 0,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  csvBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#ffffff",
    backgroundColor: designTokens.colors.success,
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  csvBtnDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  closeBtn: {
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    borderRadius: "50%",
    backgroundColor: "transparent",
    color: "#9ca3af",
    cursor: "pointer",
  },
  summary: {
    display: "flex",
    gap: "24px",
    padding: "16px 24px",
    backgroundColor: "#f8fafc",
    borderBottom: "1px solid #e5e7eb",
    fontSize: "14px",
    color: "#4b5563",
  },
  summaryStrong: {
    color: "#1f2937",
    fontWeight: "700",
  },
  body: {
    flex: 1,
    overflow: "auto",
    padding: "0 24px 24px 24px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    position: "sticky",
    top: 0,
    backgroundColor: "#ffffff",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: "600",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    padding: "14px 8px",
    borderBottom: "2px solid #e5e7eb",
  },
  thActive: {
    color: "#2563eb",
    backgroundColor: "#eff6ff",
  },
  td: {
    fontSize: "14px",
    color: "#1f2937",
    padding: "12px 8px",
    borderBottom: "1px solid #f1f5f9",
  },
  tdActive: {
    backgroundColor: "#eff6ff",
    fontWeight: "600",
  },
  loading: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9ca3af",
    fontSize: "14px",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    borderTop: "1px solid #e5e7eb",
  },
  pageInfo: {
    fontSize: "13px",
    color: "#6b7280",
  },
};

const SURVEY_COLUMNS = [
  { key: "inflow", label: "유입 경로" },
  { key: "user_type", label: "사용자 유형" },
  { key: "interest", label: "관심 서비스" },
];

const SurveyDetailModal = ({
  type,
  typeLabel,
  value,
  color,
  periodLabel,
  total,
  percent,
  detail,
  isLoading,
  page = 1,
  limit = 10,
  onPageChange,
  onDownloadCsv,
  downloading,
  onClose,
}) => {
  const respondents = total ?? detail?.total ?? 0;
  const items = detail?.items || [];
  const totalPages = Math.max(1, Math.ceil(respondents / limit));

  // 공용 Pagination 은 setPageInfo 형태를 기대하므로 onPageChange 로 변환
  const handlePageInfoChange = (updater) => {
    if (isLoading) return;
    const next =
      typeof updater === "function"
        ? updater({ page, totalPage: totalPages })
        : updater;
    if (next?.page && next.page !== page) onPageChange(next.page);
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.container} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.titleWrap}>
            <span style={{ ...styles.swatch, backgroundColor: color }} />
            <h2 style={styles.title}>
              {typeLabel} · {value}
            </h2>
          </div>
          <div style={styles.headerActions}>
            <button
              style={{
                ...styles.csvBtn,
                ...(downloading || respondents === 0 ? styles.csvBtnDisabled : {}),
              }}
              onClick={onDownloadCsv}
              disabled={downloading || respondents === 0}
            >
              <FaDownload size={12} />
              {downloading ? "다운로드 중..." : "CSV 다운로드"}
            </button>
            <button style={styles.closeBtn} onClick={onClose}>
              <FaTimes size={16} />
            </button>
          </div>
        </div>

        <div style={styles.summary}>
          <span>{periodLabel}</span>
          <span>
            응답{" "}
            <span style={styles.summaryStrong}>
              {respondents.toLocaleString()}명
            </span>
          </span>
          {percent != null && (
            <span>
              비율 <span style={styles.summaryStrong}>{percent.toFixed(1)}%</span>
            </span>
          )}
        </div>

        {isLoading ? (
          <div style={styles.loading}>로딩 중...</div>
        ) : items.length === 0 ? (
          <div style={styles.loading}>데이터가 없습니다.</div>
        ) : (
          <div style={styles.body}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>이름</th>
                  <th style={styles.th}>이메일</th>
                  {SURVEY_COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      style={{
                        ...styles.th,
                        ...(col.key === type ? styles.thActive : {}),
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                  <th style={styles.th}>가입일</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={`${item.user_id}-${idx}`}>
                    <td style={styles.td}>{item.user_name}</td>
                    <td style={styles.td}>{item.email}</td>
                    {SURVEY_COLUMNS.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          ...styles.td,
                          ...(col.key === type ? styles.tdActive : {}),
                        }}
                      >
                        {item[col.key]}
                      </td>
                    ))}
                    <td style={styles.td}>{item.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {respondents > 0 && (
          <div style={styles.footer}>
            <span style={styles.pageInfo}>
              {page} / {totalPages} 페이지 · 전체 {respondents.toLocaleString()}명
            </span>
            <Pagination
              pageInfo={{ page, totalPage: totalPages }}
              setPageInfo={handlePageInfoChange}
              syncQuery={false}
              compact
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyDetailModal;
